<script setup lang="ts">
// TODO: vue3 migration | use actual vueuse functions
// TODO: убрать повторяющийся с InputBase.vue код

import LabelError from '../LabelError.vue';
import LabelBase from '../LabelBase.vue';
import ButtonClear from './ButtonClear.vue';
import { IconError } from '../../../icons';

import { EFormItemSize } from '../../models/size';
import { computed, ref, watchEffect, useAttrs } from 'vue';
import { createMask } from 'imask';
import type { TInputMaskedProps } from '../../models/TInputMaskedProps';

const modelValue = defineModel<string>({ default: '' });

const props = withDefaults(defineProps<TInputMaskedProps>(), {
  type: 'outlined',
  size: EFormItemSize.m40,
  absoluteErrors: true,
  isRequired: false,
  error: '',
  placeholder: '',
  mask: '',
  label: '',
  clearable: true,
});

const emit = defineEmits<{
  focus: [event: Event];
  blur: [event: Event];
  input: [event: string];
}>();

const maskResolver = computed(() => {
  return props.mask ? createMask({ mask: props.mask }) : null;
});

const inputtedValue = ref('');
const isFocused = ref(false);
const isHovered = ref(false);
const inputEl = ref();

function onInput(e: Event) {
  let value = (e.target as HTMLInputElement).value;

  if (maskResolver.value) {
    maskResolver.value.resolve(value);
    value = maskResolver.value.value;
  }

  inputtedValue.value = value?.trim();
  inputEl.value.value = inputtedValue.value;
  modelValue.value = inputtedValue.value;

  emit('input', inputtedValue.value);
}

function onClear() {
  inputtedValue.value = '';
  modelValue.value = inputtedValue.value;
  inputEl.value.value = '';
}

watchEffect(() => {
  if (!inputEl.value) return;

  if (!maskResolver.value) {
    inputEl.value.value = modelValue.value;
    return;
  }

  if (inputtedValue.value === modelValue.value) return;

  maskResolver.value.resolve(modelValue.value);
  inputEl.value.value = maskResolver.value.value;
});

function onWrapperClicked() {
  inputEl.value?.focus();
}

const isClearButtonShown = computed(() => {
  if (!modelValue.value) return false;
  return isHovered.value || isFocused.value;
});

function onFocus(e: Event) {
  isFocused.value = true;
  emit('focus', e);
}

function onBlur(e: Event) {
  isFocused.value = false;
  emit('blur', e);
}
</script>

<template>
  <div
    class="input-wrapper"
    :class="{ 'input-wrapper_disabled': disabled, 'input-wrapper_absolute-errors': absoluteErrors }"
    @click="onWrapperClicked"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <LabelBase :label="label" :is-required="isRequired" :is-disabled="disabled" />

    <div
      :class="[
        'input-field-container',
        `input-field-container_${type}`,
        `input-field-container_${size}`,
        isFilled ? 'filled' : '',
        isFocused ? `input-field-container_${type}_focused` : '',
        error ? `input-field-container_error` : '',
      ]"
    >
      <input
        ref="inputEl"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        @input="onInput"
        @focus="onFocus"
        @blur="onBlur"
      />
      <ButtonClear v-if="clearable && isClearButtonShown" @click="onClear" />
      <IconError v-if="error" class="input-field-container__icon-error" />
    </div>

    <LabelError class="input-wrapper__error" :error-text="error" />
  </div>
</template>

<style lang="stylus" scoped>
.input-wrapper
  display flex
  flex-direction column
  gap 8px
  position relative
  cursor text

  &_absolute-errors
    & .input-wrapper__error
      position absolute
      bottom -18px
      left 0
      right 0

  &_disabled
    pointer-events none

.input-field-container
  display flex
  align-items center
  border-radius var(--rounding150)
  overflow hidden
  padding 6px 12px 6px 6px
  box-sizing border-box

  &.filled
    border-color transparent
    background-color var(--field-primary-rest)

    & input
      background-color var(--field-primary-rest)

  & input
    padding 0
    width 100%
    margin 0 10px
    color var(--text-primary)
    font-family var(--typography-font)
    font-size 14px
    font-weight 400
    line-height 20px
    outline none
    text-overflow ellipsis
    &:disabled
      color var(--text-disabled)
      background none

    &::placeholder
      color var(text-tertiary)

  &_m40
    height 40px

  &_l48
    height 48px

  &_outlined
    background-color #FFFFFF
    border solid 1px var(--border-regular)

    &:hover
      border solid 1px var(--border-focused)

    &_focused
      border solid 1px var(--border-focused)

  &_error
    border solid 1px var(--border-negative)!important

  &__icon-error
    color var(--icon-negative)
    font-size 20px
</style>
